import i18n, { use as i18nUse } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { posthog } from 'posthog-js';

// Translation files
import en from './locales/en.json';
import es from './locales/es.json';
import frCA from './locales/fr-CA.json';

const SHOP_LANGUAGE_STORAGE_KEY = 'shop_i18nextLng';
const LEGACY_I18NEXT_KEY = 'i18nextLng';

const resources = {
  en: { translation: en },
  es: { translation: es },
  'fr-CA': { translation: frCA },
};

// Read and migrate the stored shop language to the JSON-encoded format written by ShopLanguageController.
// - New format: JSON-encoded string (e.g. `"en"`)
// - Legacy formats: raw string under `shop_i18nextLng` or the i18next-default `i18nextLng` key
const readAndMigrateStoredLanguage = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = localStorage.getItem(SHOP_LANGUAGE_STORAGE_KEY);
    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed === 'string') {
          return parsed;
        }
      } catch {
        // Legacy raw-string value — migrate to JSON format
        localStorage.setItem(SHOP_LANGUAGE_STORAGE_KEY, JSON.stringify(raw));
        return raw;
      }
    }

    const legacy = localStorage.getItem(LEGACY_I18NEXT_KEY);
    if (legacy) {
      localStorage.setItem(SHOP_LANGUAGE_STORAGE_KEY, JSON.stringify(legacy));
      localStorage.removeItem(LEGACY_I18NEXT_KEY);
      return legacy;
    }
  } catch {
    // localStorage unavailable — fall through to detector default
  }
  return undefined;
};

i18nUse(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    saveMissing: true,
    lng: readAndMigrateStoredLanguage(),

    interpolation: {
      escapeValue: false, // React already does escaping
    },

    detection: {
      order: ['navigator', 'htmlTag'],
    },
  });

// Log missing translation keys to PostHog (non-dev only)
if (!import.meta.env.DEV) {
  i18n.on('missingKey', (lngs, namespace, key) => {
    try {
      posthog.capture('i18n_missing_key', {
        key,
        namespace,
        languages: lngs,
        path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      });
    } catch {
      // no-op
    }
  });
}
